import { ReactNode, useEffect, useRef, useState } from "react";
import {
  Authenticator,
  ThemeProvider as AmplifyThemeProvider,
  Theme,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { signInWithRedirect } from "aws-amplify/auth";

import { useTheme } from "../theme";

// Ya no se traduce nada de Amplify: el unico camino de entrada es Google y
// el boton es nuestro. `Authenticator.Provider` sigue usandose por el estado
// de sesion, pero no dibuja pantallas.

/**
 * Pantalla de entrada al portal.
 *
 * El handoff pide tematizar el Authenticator de Amplify, no construir un login
 * propio: la lógica de sesión, el refresco de tokens y el "olvidé mi
 * contraseña" ya los resuelve Amplify, y reimplementarlos sería regalar
 * superficie de error justo en lo único que no puede fallar.
 *
 * Acá viven el layout de dos paneles y el mapeo de los tokens de Amplify a las
 * variables CSS del diseño; el formulario sigue siendo el de Amplify.
 */

/** Los tokens de Amplify apuntan a las variables del diseño, así el formulario
 *  cambia solo cuando se alterna dark/light. */
const temaAmplify: Theme = {
  name: "coe-vouchers",
  tokens: {
    colors: {
      background: {
        primary: { value: "var(--panel)" },
        secondary: { value: "var(--panel2)" },
      },
      border: { primary: { value: "var(--border)" } },
      font: {
        primary: { value: "var(--text)" },
        secondary: { value: "var(--muted)" },
        interactive: { value: "var(--accent)" },
      },
      brand: {
        primary: {
          10: { value: "var(--accent-soft)" },
          80: { value: "var(--accent)" },
          90: { value: "var(--accent)" },
          100: { value: "var(--accent)" },
        },
      },
    },
    components: {
      authenticator: {
        router: {
          borderWidth: { value: "0" },
          backgroundColor: { value: "transparent" },
          boxShadow: { value: "none" },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: "var(--accent)" },
          color: { value: "var(--accent-fg)" },
          _hover: { backgroundColor: { value: "var(--accent)" } },
        },
        link: { color: { value: "var(--muted)" } },
      },
      // `backgroundColor` no existe en los tokens de fieldcontrol; el fondo
      // del input se fija por CSS mas abajo, en index.css.
      fieldcontrol: {
        color: { value: "var(--text)" },
        borderColor: { value: "var(--border)" },
        _focus: { borderColor: { value: "var(--accent)" } },
      },
      field: { label: { color: { value: "var(--muted)" } } },
      heading: { color: { value: "var(--text)" } },
    },
  },
};

/**
 * Motivo por el que Cognito rechazo el login, si lo hubo.
 *
 * Cuando `presignup` niega el acceso, Cognito devuelve al portal con
 * `?error=...&error_description=...` — y el portal lo tiraba a la basura: la
 * persona volvia a la pantalla de login sin una sola palabra de por que. Lo
 * peor que puede pasarle a alguien que acaba de intentar entrar es que no
 * pase nada.
 *
 * Se lee UNA vez al montar y se limpia la URL, para que un F5 no repita un
 * error viejo.
 */
function motivoDeRechazo(): string | null {
  const q = new URLSearchParams(window.location.search);
  const crudo = q.get("error_description") || q.get("error");
  if (!crudo) return null;

  window.history.replaceState({}, "", window.location.pathname);

  // Cognito antepone su propia cocina al mensaje del trigger. Al analista le
  // importa el mensaje, no que el trigger se llame PreSignUp.
  return crudo
    .replace(/^PreSignUp failed with error\s*/i, "")
    .replace(/^PostConfirmation failed with error\s*/i, "")
    .replace(/\.+$/, ".")
    .trim();
}

/** Logo de Google, para el boton de SSO. */
function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.5h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.6Z" />
      <path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3Z" />
      <path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6Z" />
    </svg>
  );
}

/** Entra con la cuenta de Google Workspace de Prestamype. */
function BotonGoogle() {
  const [error, setError] = useState<string | null>(null);
  // Solo se evalua en el primer render: despues la URL ya esta limpia.
  const [rechazo] = useState(motivoDeRechazo);

  async function entrar() {
    setError(null);
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (e: any) {
      // Pasa si el proveedor todavia no esta configurado en Cognito.
      setError("El acceso con Google no está disponible. Entra con tu email.");
    }
  }

  return (
    <>
      {rechazo && (
        <div className="auth-rechazo" role="alert">
          <div className="auth-rechazo-titulo">No pudimos darte acceso</div>
          <div>{rechazo}</div>
        </div>
      )}
      <button type="button" className="auth-google" onClick={entrar}>
        <GoogleIcon />
        Continuar con Google Workspace
      </button>
      {error && <div className="auth-google-error">{error}</div>}
    </>
  );
}

/**
 * Panel de entrada. **Solo Google.**
 *
 * Se saco el formulario de email y contrasena: una contrasena mas es una
 * contrasena mas que se filtra, se reusa o se olvida, y el alta ya la
 * controla la lista de Usuarios. Con Google entra quien la empresa dice que
 * puede entrar, y se va cuando la empresa le cierra la cuenta.
 */
function PanelEntrada() {
  return (
    <div className="auth-panel">
      <div>
        <div style={{ fontSize: 20, fontWeight: 600 }}>Entrar al portal</div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 3 }}>
          Con tu cuenta de Prestamype. No necesitas otra contraseña.
        </div>
      </div>

      <BotonGoogle />

      <div className="auth-pie">
        Acceso restringido al equipo COE y a la mesa de Operaciones. Tus
        permisos vienen de tu rol: analista, administrador o administrador total.
      </div>
    </div>
  );
}

export default function AuthShell({
  children,
}: {
  children: (props: { signOut?: () => void; user?: any }) => ReactNode;
}) {
  const { theme } = useTheme();
  return (
    <AmplifyThemeProvider theme={temaAmplify} colorMode={theme}>
      <Authenticator.Provider>
        <Contenido>{children}</Contenido>
      </Authenticator.Provider>
    </AmplifyThemeProvider>
  );
}

/**
 * Con sesión, la app ocupa toda la pantalla. Sin sesión, el layout de dos
 * paneles: a la izquierda qué es esto, a la derecha el formulario.
 */
/** Vuelta del redirect de Google: la URL trae ?code=...&state=... y Amplify
 *  todavia esta canjeando ese codigo por la sesion. */
function volviendoDeGoogle(): boolean {
  const q = new URLSearchParams(window.location.search);
  return q.has("code") && q.has("state");
}

function Contenido({
  children,
}: {
  children: (props: { signOut?: () => void; user?: any }) => ReactNode;
}) {
  // `user` y `signOut` salen del hook porque ya no se renderiza
  // <Authenticator>: sin formulario de email no hay nada que ese componente
  // tenga que dibujar, solo el estado que el Provider mantiene.
  const { authStatus, user, signOut } = useAuthenticator(
    (c) => [c.authStatus, c.user, c.signOut]);
  const [canjeando, setCanjeando] = useState(volviendoDeGoogle);

  // La pantalla puente es para ENTRAR, no para salir.
  //
  // Al cerrar sesión Amplify pasa por "configuring" camino a
  // "unauthenticated", y esa fracción de segundo mostraba «Entrando al
  // portal...» sobre alguien que se estaba yendo: un parpadeo con el mensaje
  // exactamente al revés. Una vez que la sesión estuvo abierta, esta pantalla
  // ya cumplió su función y no vuelve a aparecer.
  const yaEntro = useRef(false);
  if (authStatus === "authenticated") yaEntro.current = true;

  // Si el canje falla, la pantalla no puede quedar colgada: se libera y se
  // muestra el formulario con lo que haya.
  useEffect(() => {
    if (!canjeando) return;
    const t = setTimeout(() => setCanjeando(false), 8000);
    return () => clearTimeout(t);
  }, [canjeando]);

  if (authStatus === "authenticated") {
    return <>{children({ signOut, user })}</>;
  }

  // Mientras Amplify resuelve la sesion NO se muestra el formulario. Al
  // volver de Google el login aparecia un instante y desaparecia, y ese
  // parpadeo se lee como "me rechazo" justo cuando en realidad entro.
  if (!yaEntro.current && (authStatus === "configuring" || canjeando)) {
    return (
      <div className="auth-splash">
        <div className="brand-plate"><img src="/logo-coe.png" alt="" /></div>
        <div>Entrando al portal...</div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <AuthAside />
      <div className="auth-form">
        <PanelEntrada />
      </div>
    </div>
  );
}

/** Panel izquierdo: qué es esto y para quién. */
function AuthAside() {
  return (
    <aside className="auth-aside">
      <div className="auth-aside-top">
        <div className="brand-plate">
          <img src="/logo-coe.png" alt="" />
        </div>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Validación de Vouchers</span>
        <span className="auth-chip mono">COE</span>
      </div>

      <div>
        {/* Antes decia «sin abrir la consola de AWS». A quien valida
            depositos eso no le dice nada: no sabe que es la consola de AWS
            ni tiene por que saberlo. El titular ahora nombra lo que gana. */}
        <h2 className="auth-titular">
          El agente revisa cada voucher. Tú decides solo los dudosos.
        </h2>
        {/* El diseño mostraba cifras acá (2.847 procesados, 80,3% auto...).
            Van como texto: antes de iniciar sesión no hay forma de leer las
            métricas reales, y poner números de ejemplo en la portada sería
            mostrar datos inventados como si fueran del sistema. Los reales
            están en la pantalla de Métricas. */}
        <ul className="auth-lista">
          <li>
            Lee el comprobante con inteligencia artificial y lo compara con lo
            que espera Admin: monto, número de operación, cuenta y titular.
          </li>
          <li>
            Lo que cuadra o falla con claridad ya viene resuelto. A tus manos
            llega solo lo que necesita criterio.
          </li>
          <li>
            Decides con una tecla y pasas al siguiente. Queda registrado quién
            decidió, cuándo y por qué.
          </li>
        </ul>
      </div>

      <div className="auth-pie-tec mono">coe-automation · validación de vouchers</div>
    </aside>
  );
}
